import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check, X, BookOpen } from 'lucide-react';
import api from '../../utils/api';

/**
 * Multi-select of SMAART student courses, treated as skills a role expects
 * (S01 Self-Awareness Foundations, S03 Time Management Mastery, ...). Shared
 * by every job form so the field looks and behaves identically everywhere.
 *
 * value    — array of { course, courseNumber, title, category }
 * onChange — called with the new array
 */

// Display order + labels for the stage groups.
const STAGE_META = [
  { key: 'Stage1_Capacity', label: 'Capacity' },
  { key: 'Stage2_Capability', label: 'Capability' },
  { key: 'Stage3_Leadership', label: 'Leadership' },
  { key: 'PIQ', label: 'PIQ — Personal Intelligence' },
  { key: 'AIQ', label: 'AIQ — AI Readiness' },
  { key: 'SQ', label: 'SQ — Sustainability' },
  { key: 'Track', label: 'Tracks' },
  { key: 'Other', label: 'Other' }
];

// Module-level cache: the course list is the same for everyone and changes
// rarely, so every picker on the page shares one fetch.
let cachedCourses = null;
let inflight = null;
const loadCourses = async () => {
  if (cachedCourses) return cachedCourses;
  if (!inflight) {
    inflight = api.get('/courses', { params: { limit: 500, status: 'active' } })
      .then(res => {
        const list = res.data?.data || res.data?.courses || [];
        cachedCourses = list.map(c => ({
          _id: c._id,
          courseNumber: c.courseNumber || c.courseCode || '',
          title: c.title || '',
          category: c.category || '',
          stage: c.stage || (c.programmeType === 'PIQ' ? 'PIQ' : c.programmeType === 'AIQ' ? 'AIQ' : c.programmeType === 'SQ' ? 'SQ' : 'Other'),
          orderInStage: c.orderInStage || 0
        }));
        return cachedCourses;
      })
      .catch(() => { inflight = null; return []; });
  }
  return inflight;
};

const CourseSkillPicker = ({ value = [], onChange, label = 'Human Intelligence Courses', hint = 'Courses from the student programme the role expects (optional).' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [courses, setCourses] = useState(cachedCourses || []);
  const [loading, setLoading] = useState(!cachedCourses);
  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    let alive = true;
    loadCourses().then(list => { if (alive) { setCourses(list); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const selectedIds = useMemo(() => new Set(selected.map(s => String(s.course || s._id))), [selected]);

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const filtered = s
      ? courses.filter(c => c.title.toLowerCase().includes(s) || c.courseNumber.toLowerCase().includes(s) || (c.category || '').toLowerCase().includes(s))
      : courses;
    const byStage = {};
    filtered.forEach(c => { (byStage[c.stage] ||= []).push(c); });
    return STAGE_META
      .filter(m => byStage[m.key]?.length)
      .map(m => ({ ...m, items: byStage[m.key].sort((a, b) => (a.orderInStage - b.orderInStage) || a.courseNumber.localeCompare(b.courseNumber)) }));
  }, [courses, q]);

  const toggle = (c) => {
    if (selectedIds.has(String(c._id))) {
      onChange(selected.filter(s => String(s.course || s._id) !== String(c._id)));
    } else {
      onChange([...selected, { course: c._id, courseNumber: c.courseNumber, title: c.title, category: c.category }]);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
        {label} <span className="font-medium normal-case tracking-normal text-gray-400">(Optional)</span>
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="w-full min-h-[46px] flex items-center justify-between gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 bg-gray-50 dark:bg-[#001430] text-left hover:border-[#1a3884]/40 focus:outline-none focus:ring-2 focus:ring-[#1a3884] transition-all"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <BookOpen size={15} className="text-gray-400 shrink-0" />
            {selected.length === 0 ? (
              <span className="text-sm text-gray-400">{loading ? 'Loading courses...' : 'Select courses...'}</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {selected.map(s => (
                  <span
                    key={String(s.course || s._id)}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/15 dark:border-[#4f7dff]/25 px-2 py-0.5 rounded-md"
                  >
                    {s.title}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); toggle({ _id: s.course || s._id }); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggle({ _id: s.course || s._id }); } }}
                      className="hover:text-red-500 cursor-pointer"
                      aria-label={`Remove ${s.title}`}
                    >
                      <X size={11} />
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <ChevronDown size={15} className={`text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-gray-200 dark:border-[#1a3884]/40 bg-white dark:bg-[#001f40] shadow-xl overflow-hidden">
              <div className="p-2 border-b border-gray-100 dark:border-[#1a3884]/25">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    autoFocus
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by course name, code or stage..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-[#1a3884]/30 bg-gray-50 dark:bg-[#001430] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1a3884]"
                  />
                </div>
              </div>
              <div className="px-3 py-1.5 bg-gray-50/70 dark:bg-slate-900/50 flex items-center justify-between text-[11px] text-gray-500 dark:text-slate-400 font-semibold border-b border-gray-100 dark:border-slate-800">
                <span>Human Intelligence Courses</span>
                <span>{selected.length} selected</span>
              </div>
              <div className="max-h-64 overflow-y-auto custom-scrollbar py-1">
                {loading ? (
                  <div className="text-center py-4 text-xs text-gray-500">Loading courses...</div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-4 text-xs text-gray-500">No matching course.</div>
                ) : groups.map(g => (
                  <div key={g.key}>
                    <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#1a3884]/70 dark:text-[#4f7dff]/70">
                      {g.label} <span className="font-medium text-gray-400">({g.items.length})</span>
                    </div>
                    {g.items.map(c => {
                      const isSel = selectedIds.has(String(c._id));
                      return (
                        <button
                          key={c._id}
                          type="button"
                          onClick={() => toggle(c)}
                          className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between gap-3 transition-colors ${
                            isSel
                              ? 'text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 font-bold'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-[#1a3884]/5 dark:hover:bg-[#4f7dff]/10 font-semibold'
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="truncate">{c.title}</span>
                          </span>
                          {isSel && <Check size={14} className="text-[#1a3884] dark:text-[#4f7dff] shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      {hint && <p className="text-[10px] text-gray-400 dark:text-slate-500">{hint}</p>}
    </div>
  );
};

export default CourseSkillPicker;
